import * as Sentry from '@sentry/vue';

// Attachment uploads are a three-legged handshake: POST /attachments mints a row +
// key, the browser PUTs the file straight to S3, then POST /attachments/s3_confirm
// flips the row to uploaded. When the confirm leg fails the user sees a toast and
// nothing else -- the server answers 404 by design, so it raises no exception and
// produces no AppSignal incident. Report it from the client so these stay visible
// while we chase the root cause of AppSignal #2343.
//
// Sentry is initialized once per pack (see shared/sentry_config.js); these calls
// go through the global client, so nothing needs to be threaded through props.
export function reportAttachmentUploadFailure({
  reason,
  source,
  fineUploaderId,
  fileName,
  expectedKey,
  uploaderKey,
  response,
}) {
  let serializedResponse;
  try {
    // FineUploader synthesizes this object -- don't assume a shape, just record it.
    serializedResponse = JSON.stringify(response);
  } catch (e) {
    serializedResponse = String(response);
  }

  // `expectedKey` is the key we stashed when the create call resolved; `uploaderKey`
  // is the key FineUploader actually uploaded under for this file id. They should
  // always match. When they don't, a key promise resolved out of order and the URL
  // we're about to embed belongs to a *different* file -- a "saved but blank"
  // mechanism that the unique-key change does not address.
  const keyMismatch = Boolean(expectedKey && uploaderKey && expectedKey !== uploaderKey);

  const error = new Error(`Attachment upload failed: ${reason}`);
  error.name = 'AttachmentUploadFailure';

  // eslint-disable-next-line no-console
  console.error('[attachment-upload]', reason, {
    fileName, expectedKey, uploaderKey, response,
  });

  Sentry.withScope((scope) => {
    scope.setTag('attachment_upload_reason', reason);
    scope.setTag('attachment_upload_source', source);
    scope.setTag('attachment_key_mismatch', String(keyMismatch));
    scope.setContext('attachment_upload', {
      fineUploaderId,
      fileName,
      expectedKey,
      uploaderKey,
      keyMismatch,
      response: serializedResponse,
    });
    Sentry.captureException(error);
  });
}

// FineUploader's S3 module always stamps `success` on the result it hands to
// `complete`: true for any 2xx from the confirm endpoint (including our empty 204,
// where JSON parsing fails and it falls back to success), false for any rejection.
// Anything else means we never reached the confirm request at all. Callers get
// three answers rather than two so the ambiguous case can be reported instead of
// silently taking the success path.
export function classifyUploadResponse(response) {
  if (!response || typeof response !== 'object') return 'ambiguous';
  if (response.success === false) return 'failed';
  if (response.success === true) return 'succeeded';
  return 'ambiguous';
}

// Per-file key FineUploader uploaded under. Guarded because it reaches into the
// wrapper's underlying qq instance.
export function uploaderKeyFor(uploader, fineUploaderId) {
  try {
    return uploader.methods.getKey(fineUploaderId);
  } catch (e) {
    return null;
  }
}
